/**
 * SEARCH-3 — the global search palette (⌘K / Ctrl-K).
 *
 * Renders whatever `GET /api/search` returns and nothing else. The server
 * decides which entity types this user may search, which results they may
 * see, and where each result links; this component does no filtering of its
 * own. That is deliberate — the permission boundary for search lives on the
 * server (see services/global_search.py), and a client-side filter over a
 * server-filtered list is the pattern that lets the two drift apart until the
 * UI is deciding who sees what.
 *
 * Two things this handles that a naive type-ahead does not:
 *
 * - **Out-of-order responses.** Fast typing means several requests in flight;
 *   without cancellation the slowest one wins and the box shows results for a
 *   prefix the user has already moved past. Each keystroke aborts the last.
 * - **Keyboard as the primary input.** A palette people reach for with ⌘K is
 *   one they expect to drive without the mouse: ↑/↓ across groups, Enter to
 *   open, Esc to dismiss.
 *
 * CMD-1 added commands beside the results. Destinations come from the same
 * `NAV_GROUPS` the sidebar renders, filtered by the same `hasPermission`, so
 * the palette cannot offer a page the sidebar hides — two lists would drift
 * the moment a destination is added, and the palette's copy would drift
 * silently, because nobody notices a command that is quietly missing.
 *
 * Commands are matched in the browser, which is right for them and wrong for
 * results: the destination list is a static, already-permission-filtered
 * handful this client owns, while results are rows the server must decide
 * about. Filtering results here would be a second opinion on who may see
 * what.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as Icons from 'lucide-react';
import { Search, Loader2, CornerDownLeft, Moon, Sun } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { NAV_GROUPS, canReach } from '../config/navigation';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Matches MIN_TERM_LENGTH in services/global_search.py. */
export const MIN_TERM_LENGTH = 2;
const DEBOUNCE_MS = 200;

/** The backend sends a lucide icon name; fall back rather than crash the
 *  palette if a new type ships an icon this bundle doesn't have. */
function GroupIcon({ name, className }) {
  const Cmp = (name && Icons[name]) || Icons.Circle;
  return <Cmp className={className} />;
}

export default function GlobalSearch({ open, onClose }) {
  const { token, hasPermission } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const listRef = useRef(null);

  /** Every destination this user may reach, from the sidebar's own list. */
  const commands = useMemo(() => {
    const items = NAV_GROUPS.flatMap((g) => g.items)
      .filter((item) => canReach(item, hasPermission))
      .map((item) => ({
        id: `go:${item.path}`,
        label: item.name,
        sublabel: item.path,
        icon: item.icon,
        run: (nav) => nav(item.path),
      }));
    items.push({
      id: 'cmd:theme',
      label: theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode',
      sublabel: 'Appearance',
      icon: theme === 'light' ? Moon : Sun,
      run: () => toggleTheme(),
    });
    return items;
  }, [hasPermission, theme, toggleTheme]);

  const matchedCommands = useMemo(() => {
    const t = term.trim().toLowerCase();
    if (!t) return commands;
    return commands.filter((c) => c.label.toLowerCase().includes(t));
  }, [commands, term]);

  /** Commands then results, in render order — what ↑/↓ actually walks. */
  const flat = useMemo(
    () => [
      ...matchedCommands.map((c) => ({ ...c, kind: 'command' })),
      ...groups.flatMap((g) =>
        g.results.map((r) => ({ ...r, kind: 'result', type: g.type }))),
    ],
    [matchedCommands, groups]
  );

  useEffect(() => {
    if (open) {
      setTerm(''); setGroups([]); setError(null); setActive(0);
      // Focus after paint, or the browser hands focus back to the trigger.
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const q = term.trim();
    if (q.length < MIN_TERM_LENGTH) {
      abortRef.current?.abort();
      setGroups([]); setLoading(false); setError(null);
      return undefined;
    }
    const handle = setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/search`, {
          params: { q, limit_per_type: 5 },
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        setGroups(res.data.groups || []);
        setError(null);
        setActive(0);
      } catch (err) {
        if (axios.isCancel(err) || err.name === 'CanceledError') return;
        setGroups([]);
        setError('Search is unavailable right now.');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [term, open, token]);

  const choose = useCallback((row) => {
    if (!row) return;
    if (row.kind === 'command') {
      onClose?.();
      row.run(navigate);
      return;
    }
    if (!row.href) return;
    onClose?.();
    navigate(row.href);
  }, [navigate, onClose]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); onClose?.(); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (flat.length ? (i + 1) % flat.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (flat.length ? (i - 1 + flat.length) % flat.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(flat[active]);
    }
  };

  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-index="${active}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  if (!open) return null;

  const short = term.trim().length > 0 && term.trim().length < MIN_TERM_LENGTH;
  // Results are indexed after the commands, so ↑/↓ walks one continuous list.
  let index = matchedCommands.length - 1;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center px-4 pt-[10vh] bg-slate-900/50 backdrop-blur-sm"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="global-search-overlay"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search Northline"
        className="w-full max-w-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl overflow-hidden"
      >
        <div className="flex items-center gap-3 px-4 border-b border-slate-200 dark:border-slate-700">
          <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
          <input
            ref={inputRef}
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search or jump to…"
            aria-label="Search term"
            className="flex-1 h-12 bg-transparent text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none"
            data-testid="global-search-input"
          />
          {loading && <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />}
        </div>

        <div ref={listRef} className="max-h-[55vh] overflow-y-auto" data-testid="global-search-results">
          {matchedCommands.length > 0 && (
            <div>
              <div className="px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Go to
              </div>
              {matchedCommands.map((cmd, n) => {
                const Icon = cmd.icon;
                return (
                  <button
                    key={cmd.id}
                    type="button"
                    data-index={n}
                    onMouseEnter={() => setActive(n)}
                    onClick={() => choose({ ...cmd, kind: 'command' })}
                    className={`w-full text-left px-4 py-2 flex items-center gap-3 ${
                      n === active
                        ? 'bg-slate-100 dark:bg-slate-700'
                        : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'
                    }`}
                    data-testid={`global-search-command-${cmd.id}`}
                  >
                    {Icon && <Icon className="w-4 h-4 text-slate-400 flex-shrink-0" />}
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm text-slate-900 dark:text-white truncate">
                        {cmd.label}
                      </span>
                    </span>
                    {n === active && (
                      <CornerDownLeft className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
          {short && (
            <p className="px-4 py-4 text-sm text-slate-500 dark:text-slate-400">
              Keep typing — records need at least {MIN_TERM_LENGTH} characters.
            </p>
          )}
          {error && (
            <p className="px-4 py-6 text-sm text-red-600 dark:text-red-400">{error}</p>
          )}
          {!short && !error && !loading && term.trim().length >= MIN_TERM_LENGTH && flat.length === 0 && (
            <p className="px-4 py-6 text-sm text-slate-500 dark:text-slate-400">
              No matches for “{term.trim()}”.
            </p>
          )}
          {groups.map((group) => (
            <div key={group.type}>
              <div className="flex items-center gap-2 px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                <GroupIcon name={group.icon} className="w-3.5 h-3.5" />
                {group.label}
              </div>
              {group.results.map((row) => {
                index += 1;
                const i = index;
                return (
                  <button
                    key={`${group.type}-${row.id}`}
                    type="button"
                    data-index={i}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => choose({ ...row, type: group.type })}
                    className={`w-full text-left px-4 py-2 flex items-center justify-between gap-3 ${
                      i === active
                        ? 'bg-slate-100 dark:bg-slate-700'
                        : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'
                    }`}
                    data-testid={`global-search-result-${group.type}-${row.id}`}
                  >
                    <span className="min-w-0">
                      <span className="block text-sm text-slate-900 dark:text-white truncate">
                        {row.label}
                      </span>
                      {row.sublabel && (
                        <span className="block text-xs text-slate-500 dark:text-slate-400 truncate">
                          {row.sublabel}
                        </span>
                      )}
                    </span>
                    {i === active && (
                      <CornerDownLeft className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <div className="px-4 py-2 border-t border-slate-200 dark:border-slate-700 text-[11px] text-slate-400 flex items-center gap-3">
          <span>↑↓ to navigate</span>
          <span>↵ to open</span>
          <span>esc to close</span>
        </div>
      </div>
    </div>
  );
}
